"use client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useI18n } from "../lib/i18n";

type InstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };

type ToastContextValue = { toast: (message: string, tone?: "success" | "error") => void };
const ToastContext = createContext<ToastContextValue>({ toast: () => undefined });
export const useToast = () => useContext(ToastContext);

export function Providers({ children }: { children: React.ReactNode }) {
  const { t, locale } = useI18n();
  const [queryClient] = useState(() => new QueryClient({ defaultOptions: { queries: { staleTime: 30_000, retry: 1, networkMode: "offlineFirst" }, mutations: { networkMode: "online" } } }));
  const [message, setMessage] = useState<{ text: string; tone: "success" | "error" } | null>(null);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const toast = useCallback((text: string, tone: "success" | "error" = "success") => { setMessage({ text, tone }); window.setTimeout(() => setMessage(null), 2800); }, []);
  const value = useMemo(() => ({ toast }), [toast]);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      if (process.env.NODE_ENV === "production") navigator.serviceWorker.register("/sw.js").catch(() => undefined);
      else {
        void navigator.serviceWorker.getRegistrations().then((registrations) => Promise.all(registrations.map((registration) => registration.unregister()))).catch(() => undefined);
        if ("caches" in window) void caches.keys().then((keys) => Promise.all(keys.filter((key) => key.startsWith("mylnitsa-")).map((key) => caches.delete(key)))).catch(() => undefined);
      }
    }
    const capture = (event: Event) => {
      event.preventDefault();
      const dismissedAt = Number(localStorage.getItem("mylnitsa-install-dismissed") ?? 0);
      if (Date.now() - dismissedAt > 7 * 86_400_000) setInstallPrompt(event as InstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", capture);
    return () => window.removeEventListener("beforeinstallprompt", capture);
  }, []);
  const dismissInstall = () => { localStorage.setItem("mylnitsa-install-dismissed", String(Date.now())); setInstallPrompt(null); };
  const install = async () => { if (!installPrompt) return; await installPrompt.prompt(); await installPrompt.userChoice; setInstallPrompt(null); };
  return <QueryClientProvider client={queryClient}><ToastContext.Provider value={value}>{children}{message && <div className={`toast ${message.tone}`} role="status">{message.text}</div>}{installPrompt && <aside className="install-prompt" role="dialog" aria-labelledby="install-title"><div className="brand-mark">М</div><div><b id="install-title">{t("installApp")}</b><p>{t("installHint")}</p></div><button className="ghost-button" onClick={dismissInstall}>{t("later")}</button><button className="primary-button compact" onClick={install}>{t("install")}</button></aside>}</ToastContext.Provider></QueryClientProvider>;
}
